import _ from 'lodash';
import { ADMIN_SCOPES } from '../constants/admin_scope_constants';
import { parseJson } from './helpers';

export const getScriptArgsSchema = config => {
  const rawArgs = parseJson(config?.args);
  if (Array.isArray(rawArgs)) return rawArgs;
  if (!rawArgs || typeof rawArgs !== 'object') return [];

  return Object.entries(rawArgs).map(([name, value]) => ({
    name,
    description: typeof value === 'string' ? value : value?.description || '',
    flag: value?.flag ?? name,
    type: value?.type,
    takesValue: value?.takesValue,
    options: value?.options,
    required: Boolean(value?.required)
  }));
};

export const getScriptArgOptions = arg => (Array.isArray(arg?.options) ? arg.options.map(String) : []);

export const parseEntityListInput = text =>
  _.uniqBy(
    String(text || '')
      .split(/[\s,]+/)
      .map(token => token.replace(/^@/, ''))
      .filter(Boolean),
    token => token.toLowerCase()
  );

export const isEntityIdInput = token => /^\d+$/.test(token);

// Brands are only addressable by id, so a pasted brand name still flows into the search box as a query.
export const shouldResolvePastedEntityList = (tokens, type) => tokens.length > 0 && (type === 'user' || tokens.every(isEntityIdInput));

export const getScriptRunnerPath = (configId, runId) => ['/scripts', configId, runId].filter(Boolean).join('/');

export const searchScriptConfigs = (configs, query) => {
  const term = query.trim().toLowerCase();
  if (!term) return configs;
  return configs.filter(config => [config.name, config.scriptPath, config.description].some(field => (field || '').toLowerCase().includes(term)));
};

export const getScriptAllowedScopes = config => {
  const scopes = parseJson(config?.allowedScopes);
  return Array.isArray(scopes) ? scopes : [];
};
export const getInvalidScriptConfigScopes = scopes => scopes.filter(scope => !Object.values(ADMIN_SCOPES).includes(scope));

export const getScriptAllowedUserIds = config => {
  const User_ids = parseJson(config?.allowedUserIds);
  return Array.isArray(User_ids) ? User_ids : [];
};

export const scriptConfigToForm = config => ({
  ...config,
  description: config.description || '',
  allowedScopes: getScriptAllowedScopes(config).join(', '),
  allowedUserIds: getScriptAllowedUserIds(config).join(', '),
  maxRuntimeSeconds: String(config.maxRuntimeSeconds ?? 3600),
  _originalDescription: config.description,
  _originalAllowedScopesWasNull: config.allowedScopes == null,
  _originalAllowedUserIdsWasNull: config.allowedUserIds == null,
  _originalArgsWasNull: config.args == null,
  args: getScriptArgsSchema(config).map(arg => ({
    ...arg,
    name: arg.name || '',
    flag: arg.flag || '',
    description: arg.description || '',
    type: arg.type || 'string',
    required: Boolean(arg.required),
    takesValue: arg.takesValue !== false,
    options: Array.isArray(arg.options) ? arg.options.join(', ') : ''
  }))
});

export const buildScriptConfigPayload = form => {
  const allowedScopes = form.allowedScopes.split(',').map(value => value.trim()).filter(Boolean);
  const allowedUserIds = form.allowedUserIds
    .split(',')
    .map(value => value.trim())
    .filter(Boolean)
    .map(Number);
  const args = form.args.map(arg => {
    const { options, ...otherFields } = arg;
    return {
      ...otherFields,
      name: arg.name.trim(),
      flag: arg.flag.trim(),
      description: arg.description.trim(),
      type: arg.type,
      required: Boolean(arg.required),
      takesValue: arg.type === 'boolean' ? false : Boolean(arg.takesValue),
      ...(arg.type === 'option' ? { options: (options || '').split(',').map(value => value.trim()).filter(Boolean) } : {})
    };
  });

  return {
    scriptPath: form.scriptPath.trim(),
    name: form.name.trim(),
    description: form.description.trim() || (form._originalDescription === '' ? '' : null),
    allowedScopes: allowedScopes.length ? allowedScopes : form._originalAllowedScopesWasNull ? null : [],
    allowedUserIds: allowedUserIds.length ? allowedUserIds : form._originalAllowedUserIdsWasNull ? null : [],
    mustDryRun: form.mustDryRun,
    maxRuntimeSeconds: Number(form.maxRuntimeSeconds),
    isEnabled: form.isEnabled,
    args: args.length ? args : form._originalArgsWasNull ? null : []
  };
};

export const canCurrentUserRunScript = (config, currentUser) => {
  if (!config || !currentUser) return false;
  const userScopes = typeof currentUser.scopes === 'string' ? currentUser.scopes.split(',').filter(Boolean) : currentUser.scopes || [];
  if (userScopes.includes(ADMIN_SCOPES.ALL)) return true;

  const allowedScopes = getScriptAllowedScopes(config);
  const allowedUserIds = getScriptAllowedUserIds(config);
  return allowedScopes.some(scope => userScopes.includes(scope)) || allowedUserIds.some(User_id => Number(User_id) === Number(currentUser?.id));
};
